/**
 * Schema Validation Test Page
 * Demonstrates AJV schema validation with intentional malformed data
 */

import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { validator } from '@/lib/validator';
import { CheckCircle2, XCircle, AlertTriangle, Play } from 'lucide-react';
import { safeJsonParse } from '@/lib/utils';

interface TestResult {
  name: string;
  schema: string;
  valid: boolean;
  errors?: string;
  timestamp: Date;
}

export default function SchemaValidationTest() {
  const [results, setResults] = useState<TestResult[]>([]);
  const [customJson, setCustomJson] = useState('');
  const [selectedSchema, setSelectedSchema] = useState<'agent' | 'email' | 'calendar' | 'oauth'>('agent');

  const runTests = () => {
    const testResults: TestResult[] = [];

    // Test 1: Valid Agent Response
    testResults.push({
      name: 'Valid Agent Response',
      schema: 'AgentResponse',
      ...validator.validateAgentResponse({
        type: 'action',
        action: {
          type: 'email_send',
          parameters: { to: ['test@example.com'], subject: 'Test', body: 'Hello' },
          confidence: 95
        }
      }),
      timestamp: new Date()
    });

    // Test 2: Invalid Agent Response (missing type)
    testResults.push({
      name: 'Invalid Agent Response - Missing Type',
      schema: 'AgentResponse',
      ...validator.validateAgentResponse({
        action: { type: 'email_send', parameters: {} }
      }),
      timestamp: new Date()
    });

    // Test 3: Valid Email Message
    testResults.push({
      name: 'Valid Email Message',
      schema: 'EmailMessage',
      ...validator.validateEmailMessage({
        id: 'msg_123',
        user_id: 'user_456',
        account_id: 'acc_789',
        source: 'outlook',
        provider_message_id: 'prov_123',
        subject: 'Test Email',
        sender_email: 'sender@example.com',
        sender_name: 'John Doe',
        to_recipients: JSON.stringify(['recipient@example.com']),
        body_text: 'Email body',
        received_date: new Date().toISOString(),
        is_read: '0',
        is_starred: '0',
        has_attachments: '0',
        attachment_count: 0,
        size_estimate: 1024,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }),
      timestamp: new Date()
    });

    // Test 4: Invalid Email Message (missing required fields)
    testResults.push({
      name: 'Invalid Email Message - Missing Required Fields',
      schema: 'EmailMessage',
      ...validator.validateEmailMessage({
        id: 'msg_123',
        subject: 'Test Email'
        // Missing sender_email and other required fields
      }),
      timestamp: new Date()
    });

    // Test 5: Invalid Email Message (wrong type)
    testResults.push({
      name: 'Invalid Email Message - Wrong Type',
      schema: 'EmailMessage',
      ...validator.validateEmailMessage({
        id: 'msg_123',
        user_id: 'user_456',
        subject: 'Test',
        sender_email: 'not-an-email', // Invalid email format
        attachment_count: 'not-a-number' // Wrong type
      }),
      timestamp: new Date()
    });

    // Test 6: Valid Calendar Event
    testResults.push({
      name: 'Valid Calendar Event',
      schema: 'CalendarEvent',
      ...validator.validateCalendarEvent({
        id: 'evt_123',
        user_id: 'user_456',
        account_id: 'acc_789',
        source: 'outlook',
        provider_event_id: 'prov_123',
        title: 'Team Meeting',
        description: 'Quarterly planning',
        start_time: new Date().toISOString(),
        end_time: new Date(Date.now() + 3600000).toISOString(),
        timezone: 'America/New_York',
        is_all_day: '0',
        status: 'confirmed',
        attendee_count: 5,
        event_type: 'meeting',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }),
      timestamp: new Date()
    });

    // Test 7: Invalid Calendar Event (invalid status)
    testResults.push({
      name: 'Invalid Calendar Event - Invalid Status',
      schema: 'CalendarEvent',
      ...validator.validateCalendarEvent({
        id: 'evt_123',
        user_id: 'user_456',
        title: 'Meeting',
        start_time: new Date().toISOString(),
        status: 'invalid_status', // Should be confirmed, tentative, or cancelled
        attendee_count: -5 // Negative count
      }),
      timestamp: new Date()
    });

    // Test 8: Valid OAuth Token Response
    testResults.push({
      name: 'Valid OAuth Token Response',
      schema: 'OAuthTokenResponse',
      ...validator.validateOAuthTokenResponse({
        access_token: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...',
        refresh_token: 'refresh_abc123',
        token_type: 'Bearer',
        expires_in: 3600,
        scope: 'email calendar'
      }),
      timestamp: new Date()
    });

    // Test 9: Invalid OAuth Token Response (missing access_token)
    testResults.push({
      name: 'Invalid OAuth Token Response - Missing Access Token',
      schema: 'OAuthTokenResponse',
      ...validator.validateOAuthTokenResponse({
        token_type: 'Bearer',
        expires_in: 3600
        // Missing access_token
      }),
      timestamp: new Date()
    });

    // Test 10: Valid Email Action Params
    testResults.push({
      name: 'Valid Email Action Params',
      schema: 'EmailActionParams',
      ...validator.validateEmailActionParams({
        to: ['recipient@example.com', 'another@example.com'],
        subject: 'Important Update',
        body: 'Please review the attached document.'
      }),
      timestamp: new Date()
    });

    // Test 11: Invalid Email Action Params (invalid email format)
    testResults.push({
      name: 'Invalid Email Action Params - Invalid Email Format',
      schema: 'EmailActionParams',
      ...validator.validateEmailActionParams({
        to: 'not-an-array',
        subject: 'Test'
      }),
      timestamp: new Date()
    });

    setResults(testResults);
  };

  const testCustomJson = () => {
    try {
      const data = safeJsonParse(customJson);
      let result;
      
      switch (selectedSchema) {
        case 'agent':
          result = validator.validateAgentResponse(data);
          break;
        case 'email':
          result = validator.validateEmailMessage(data);
          break;
        case 'calendar':
          result = validator.validateCalendarEvent(data);
          break;
        case 'oauth':
          result = validator.validateOAuthTokenResponse(data);
          break;
        default:
          result = { valid: false, errorMessage: 'Unknown schema' };
      }

      setResults([{
        name: `Custom ${selectedSchema} Test`,
        schema: selectedSchema,
        ...result,
        timestamp: new Date()
      }, ...results]);
    } catch (error) {
      setResults([{
        name: 'Custom JSON Test',
        schema: selectedSchema,
        valid: false,
        errors: error instanceof Error ? error.message : 'Invalid JSON',
        timestamp: new Date()
      }, ...results]);
    }
  };

  const clearResults = () => {
    setResults([]);
  };

  const successCount = results.filter(r => r.valid).length;
  const failureCount = results.filter(r => !r.valid).length;

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Schema Validation Test Suite</h1>
          <p className="text-muted-foreground mt-2">
            AJV-powered validation with explicit malformed data rejection
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={runTests} size="lg">
            <Play className="mr-2 h-4 w-4" />
            Run All Tests
          </Button>
          <Button onClick={clearResults} variant="outline" size="lg">
            Clear Results
          </Button>
        </div>
      </div>

      {results.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Total Tests</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{results.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-sm text-green-600">Passed</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">{successCount}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-sm text-red-600">Failed</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-red-600">{failureCount}</div>
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Custom JSON Validation</CardTitle>
          <CardDescription>
            Test your own JSON data against validation schemas
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Button
              variant={selectedSchema === 'agent' ? 'default' : 'outline'}
              onClick={() => setSelectedSchema('agent')}
            >
              Agent Response
            </Button>
            <Button
              variant={selectedSchema === 'email' ? 'default' : 'outline'}
              onClick={() => setSelectedSchema('email')}
            >
              Email Message
            </Button>
            <Button
              variant={selectedSchema === 'calendar' ? 'default' : 'outline'}
              onClick={() => setSelectedSchema('calendar')}
            >
              Calendar Event
            </Button>
            <Button
              variant={selectedSchema === 'oauth' ? 'default' : 'outline'}
              onClick={() => setSelectedSchema('oauth')}
            >
              OAuth Token
            </Button>
          </div>
          <Textarea
            placeholder="Paste your JSON data here..."
            value={customJson}
            onChange={(e) => setCustomJson(e.target.value)}
            rows={8}
            className="font-mono text-sm"
          />
          <Button onClick={testCustomJson}>
            Validate Custom JSON
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Test Results</CardTitle>
          <CardDescription>
            Validation results with detailed error messages
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[600px]">
            <div className="space-y-3">
              {results.length === 0 ? (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    No tests run yet. Click "Run All Tests" to start validation testing.
                  </AlertDescription>
                </Alert>
              ) : (
                results.map((result, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border ${
                      result.valid
                        ? 'bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800'
                        : 'bg-red-50 border-red-200 dark:bg-red-950 dark:border-red-800'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {result.valid ? (
                            <CheckCircle2 className="h-5 w-5 text-green-600" />
                          ) : (
                            <XCircle className="h-5 w-5 text-red-600" />
                          )}
                          <span className="font-semibold">{result.name}</span>
                          <Badge variant="outline" className="text-xs">
                            {result.schema}
                          </Badge>
                        </div>
                        {result.errors && (
                          <div className="text-sm text-muted-foreground mt-2 font-mono">
                            {result.errors}
                          </div>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {result.timestamp.toLocaleTimeString()}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
